import { Combobox, Drawer, FloatingPanel, Tooltip, todayCivilDate } from '@hue/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Outlet, useLocation } from '@tanstack/react-router';
import { PageHelpProvider } from './page-help.js';
import {
  ArrowLeft,
  BookOpen,
  ChevronDown,
  Clock3,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  ShieldAlert,
} from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { sessionQuery } from '../lib/session.js';
import { refreshSession } from '../lib/session.js';
import {
  businessUnitApi,
  getApiError,
  preferencesApi,
  type SessionUser,
  type UserAppearance,
} from '../lib/api.js';
import { EMPTY_LOGOS, useBrand } from './brand.js';
import { CompanyIdentityMark, HueSystemMark } from './company-identity-mark.js';
import { resolveTimeZone } from '../lib/time-zone.js';
import { ClosedPeriodProvider } from './closed-period-provider.js';
import { CompanyScopeProvider, type CompanyScope } from './company-context.js';
import {
  companyHomeSection,
  navigationItemsFor,
  sectionsVisibleTo,
  settingsBlocksVisibleTo,
  type CompanyNavigationItem,
  type CompanySection,
} from './company-sections.js';
import { GuideMenu } from './guides/guide-menu.js';
import { GuidesProvider } from './guides/guide-provider.js';
import { useScreenVisit } from './use-screen-visit.js';
import { ThemeToggle } from './theme-toggle.js';
import { TimeZoneSettings } from './time-zone-settings.js';
import { useLeaveCompany } from './use-leave-company.js';
import { useLogout } from './use-logout.js';
import { ContextSwitcher } from './context-switcher.js';
import { OperationalPeriodBar, OperationalPeriodProvider } from './operational-period.js';

/// A casca da empresa: menu, cabeçalho, faixa de acesso root e o rodapé de conta. O miolo é o
/// `<Outlet/>` da rota — menos quando alguém entrega `content`, que hoje é só o 404: a tela de
/// endereço inexistente não é filha de nenhuma seção, mas continua precisando do menu ao lado.
export function CompanyLayout({ content }: { content?: ReactNode }) {
  const queryClient = useQueryClient();
  const session = useQuery(sessionQuery);
  const logout = useLogout();
  const { leaving, leave } = useLeaveCompany();
  const [menuOpen, setMenuOpen] = useState(false);
  const [timeZoneSettingsOpen, setTimeZoneSettingsOpen] = useState(false);
  const [collapsed, setCollapsed] = useNavigationCollapsed(session.data);
  const pathname = useLocation({ select: (location) => location.pathname });

  /// O portão da rota já provou que existe sessão com empresa. Este retorno cobre o instante entre
  /// sair (da conta ou da empresa) e ser levado para o próximo lugar, e não é tela: pintar
  /// "carregando" aqui piscaria no meio de uma saída que já deu certo.
  const user = session.data;
  const company = user?.currentTenant;
  /// Antes do retorno acima, porque efeito não pode nascer e morrer conforme a sessão chega. Sem
  /// empresa em mãos o endereço não casa com seção nenhuma e nada é contado.
  useScreenVisit(company?.key ?? '');
  const brand = useBrand(company ? `${company.key}:${company.activeBusinessUnitId ?? ''}` : null);
  const logos = brand.data?.active ?? EMPTY_LOGOS;
  if (!user || !company) return null;

  const isRoot = user.platformRole === 'ROOT';
  const can = (permission: string) => isRoot || (company.permissions ?? []).includes(permission);
  const roleKeys = company.roleKeys ?? [];
  const displayTimeZone = resolveTimeZone(user.timeZone, company.timeZone);
  const scope: CompanyScope = {
    user,
    company,
    isRoot,
    can,
    timeZone: displayTimeZone,
    today: todayCivilDate(displayTimeZone),
  };

  const accessLabel = isRoot
    ? 'Root em atuação'
    : roleKeys.includes('TENANT_OWNER')
      ? 'Proprietário'
      : roleKeys.includes('TENANT_ADMIN')
        ? 'Administrador'
        : 'Pessoa com acesso';

  /// No alto do menu, a marca do produto; a da empresa fica no centro do topo (ADR 0040, adendo).
  const identity = (compact = false) => <HueSystemMark collapsed={compact} />;

  /// As seções que esta pessoa pode ver, na ordem do catálogo, com Início na frente.
  ///
  /// A lista vem de `company-sections.ts` porque a tela de início monta os mesmos atalhos a partir
  /// dela: duas listas divergiriam, e o sintoma seria uma tela chamada de um jeito no menu e de
  /// outro no cartão. Aqui ela é montada uma vez e usada duas — na barra lateral do desktop e na
  /// gaveta do celular —, porque duplicar o JSX foi como as duas começaram a divergir.
  ///
  /// Cada uma é um link para o endereço dela: quem quiser abrir Auditoria numa aba nova, ou mandar
  /// o endereço para alguém da mesma empresa, consegue. O que a pessoa não pode ver não aparece
  /// aqui **e** não abre pelo endereço — o portão de cada rota repete a conta.
  ///
  /// As páginas que administram a empresa não entram aqui: moram atrás da engrenagem (ADR 0041),
  /// e o menu fica com o que se usa no dia a dia.
  const items = navigationItemsFor([companyHomeSection, ...sectionsVisibleTo(can, 'operation')]);
  const settingsOpen = pathname.startsWith(`/companies/${company.key}/settings`);
  const documentationOpen = pathname === `/companies/${company.key}/documentation`;
  const settingsBlocks = settingsBlocksVisibleTo(can);

  const navigation = (onNavigate?: () => void, compact = false, toggle?: ReactNode) => (
    <nav
      aria-label={`Navegação de ${company.displayName}`}
      className={`flex-1 space-y-1 py-4 ${compact ? 'px-3' : 'px-2'}`}
    >
      {toggle ? (
        <div className={`flex pb-1 ${compact ? 'justify-center' : 'justify-end'}`}>{toggle}</div>
      ) : null}
      {items.map((item) => (
        <CompanyNavigationEntry
          key={item.kind === 'section' ? item.section.key : item.key}
          item={item}
          companyKey={company.key}
          onNavigate={onNavigate}
          compact={compact}
        />
      ))}
    </nav>
  );

  /// Configurações ocupa a mesma coluna do menu operacional. Uma segunda barra dentro do conteúdo
  /// comprimira justamente os formulários mais largos; voltar leva ao início da empresa e restaura
  /// a navegação diária. No celular a mesma lista aparece na gaveta, enquanto a página conserva a
  /// lista de escolha compacta para trocar de seção sem reabri-la.
  const settingsNavigation = (onNavigate?: () => void) => (
    <nav aria-label="Configurações" className="space-y-5 px-2 py-4">
      <Link
        to="/companies/$companyKey"
        params={{ companyKey: company.key }}
        activeOptions={{ exact: true }}
        onClick={() => onNavigate?.()}
        className="flex h-11 w-full items-center gap-3 rounded-xl border border-transparent px-3 text-sm font-semibold text-content-muted transition hover:bg-content/5 hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
      >
        <ArrowLeft className="size-4 shrink-0" aria-hidden="true" />
        Voltar
      </Link>
      <p className="px-3 text-lg font-bold tracking-[-.02em]">Configurações</p>
      {settingsBlocks.map((block) => (
        <div key={block.key}>
          <p className="mb-1 px-3 text-xs font-semibold text-content-faint">{block.label}</p>
          <div className="space-y-0.5">
            {block.sections.map((section) => (
              <CompanyNavigationLink
                key={section.key}
                section={section}
                companyKey={company.key}
                onNavigate={onNavigate}
                child
              />
            ))}
          </div>
        </div>
      ))}
    </nav>
  );

  const collapseButton = (
    <Tooltip content={collapsed ? 'Abrir menu' : 'Recolher menu'} side="right">
      <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        aria-label={collapsed ? 'Abrir menu' : 'Recolher menu'}
        aria-expanded={!collapsed}
        className="grid size-10 shrink-0 place-items-center rounded-xl border border-surface-border text-content-secondary transition hover:border-brand-500/50 hover:text-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
      >
        {collapsed ? (
          <PanelLeftOpen className="size-4" aria-hidden="true" />
        ) : (
          <PanelLeftClose className="size-4" aria-hidden="true" />
        )}
      </button>
    </Tooltip>
  );

  /// O rodapé do menu recolhido: só ícones, empilhados, cada um com o nome na dica.
  const compactAccount = (
    <div className="flex shrink-0 flex-col items-center gap-2 border-t border-surface-border p-3">
      <Tooltip content={`${user.firstName} ${user.lastName} · ${accessLabel}`} side="right">
        <button
          type="button"
          onClick={() => setTimeZoneSettingsOpen(true)}
          aria-label="Configurar fuso horário"
          className="grid size-10 place-items-center rounded-full bg-brand-500 text-sm font-bold text-on-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
        >
          {user.firstName.slice(0, 1).toUpperCase()}
        </button>
      </Tooltip>
      <ContextSwitcher compact />
      <ThemeToggle user={user} />
      <Tooltip content="Configurações" side="right">
        <SettingsLink companyKey={company.key} />
      </Tooltip>
      <Tooltip content="Ajuda do sistema" side="right">
        <DocumentationLink companyKey={company.key} />
      </Tooltip>
      <Tooltip content="Sair" side="right">
        <button
          type="button"
          onClick={() => void logout()}
          aria-label="Sair"
          className="grid size-10 place-items-center rounded-xl border border-surface-border text-content-secondary transition hover:border-danger/50 hover:text-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
        >
          <LogOut className="size-4" aria-hidden="true" />
        </button>
      </Tooltip>
    </div>
  );

  const account = () => (
    <div className="shrink-0 border-t border-surface-border p-3">
      <button
        type="button"
        onClick={() => setTimeZoneSettingsOpen(true)}
        aria-label="Configurar fuso horário"
        className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left transition hover:bg-surface-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
      >
        <span className="grid size-9 shrink-0 place-items-center rounded-full bg-brand-500 text-sm font-bold text-on-brand">
          {user.firstName.slice(0, 1).toUpperCase()}
        </span>
        <div className="min-w-0 flex-1">
          <strong className="block truncate text-sm font-medium">
            {user.firstName} {user.lastName}
          </strong>
          <span className="block truncate text-xs text-brand-400">{accessLabel}</span>
        </div>
        <Clock3 className="size-4 shrink-0 text-content-faint" />
      </button>
      <div className="mt-2">
        <ContextSwitcher />
      </div>
      <div className="mt-2 flex gap-2">
        <ThemeToggle user={user} />
        <Tooltip content="Configurações">
          <SettingsLink companyKey={company.key} onNavigate={() => setMenuOpen(false)} />
        </Tooltip>
        <Tooltip content="Ajuda do sistema">
          <DocumentationLink companyKey={company.key} onNavigate={() => setMenuOpen(false)} />
        </Tooltip>
        <button
          type="button"
          onClick={() => void logout()}
          className="flex h-10 flex-1 items-center justify-center gap-2 rounded-xl border border-surface-border text-xs font-medium text-content-secondary transition hover:border-danger/50 hover:text-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
        >
          <LogOut className="size-4" />
          Sair
        </button>
      </div>
    </div>
  );

  return (
    <CompanyScopeProvider value={scope}>
      <OperationalPeriodProvider key={company.key} companyKey={company.key} today={scope.today}>
        <ClosedPeriodProvider>
          <GuidesProvider>
            <div
              className={`min-h-screen bg-surface lg:grid ${
                collapsed && !settingsOpen && !documentationOpen
                  ? 'lg:grid-cols-[76px_minmax(0,1fr)]'
                  : 'lg:grid-cols-[260px_minmax(0,1fr)]'
              }`}
            >
              <aside
                aria-label="Menu lateral"
                data-collapsed={collapsed && !settingsOpen && !documentationOpen}
                className="sticky top-0 hidden h-screen flex-col border-r border-surface-border bg-surface-card lg:flex"
              >
                <div
                  className={`flex h-16 shrink-0 items-center border-b border-surface-border ${
                    collapsed && !settingsOpen && !documentationOpen
                      ? 'justify-center px-2'
                      : 'px-4'
                  }`}
                >
                  {identity(collapsed && !settingsOpen && !documentationOpen)}
                </div>
                {/* A lista rola, o rodapé fica. Sem `min-h-0` o filho flexível não encolhe abaixo do
              próprio conteúdo: bastava expandir uma seção para a conta de quem está logado ser
              empurrada para fora da tela. É o mesmo arranjo que a gaveta do celular já usava. */}
                {/* Abrir e recolher moram no topo da lista, antes de Início: no rodapé, ao lado de Sair,
              um clique errado encerrava a sessão de quem só queria o menu de volta. */}
                <div className="min-h-0 flex-1 overflow-y-auto">
                  {settingsOpen
                    ? settingsNavigation()
                    : navigation(undefined, collapsed && !documentationOpen, collapseButton)}
                </div>
                <GuideMenu compact={collapsed && !settingsOpen && !documentationOpen} />
                {collapsed && !settingsOpen && !documentationOpen ? compactAccount : account()}
              </aside>

              <Drawer
                open={menuOpen}
                onOpenChange={setMenuOpen}
                title={`Menu de ${company.displayName}`}
              >
                <div className="flex h-16 shrink-0 items-center border-b border-surface-border pl-4 pr-14">
                  {identity()}
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto">
                  {settingsOpen
                    ? settingsNavigation(() => setMenuOpen(false))
                    : navigation(() => setMenuOpen(false))}
                </div>
                <GuideMenu onOpened={() => setMenuOpen(false)} />
                {account()}
              </Drawer>

              <div className="min-w-0">
                {/* O topo leva a identidade da empresa no centro; no celular, também o botão do menu.
              Fuso, tema e sair moram no menu, junto de quem está logado — encher a barra de ícones
              sem rótulo é como ela ficou ilegível. */}
                <header className="sticky top-0 z-30 grid h-16 grid-cols-[2.5rem_minmax(0,1fr)_2.5rem] items-center gap-3 border-b border-surface-border bg-surface-card px-4 sm:px-8 lg:grid-cols-[minmax(0,1fr)]">
                  <button
                    type="button"
                    onClick={() => setMenuOpen(true)}
                    aria-label="Abrir menu"
                    aria-expanded={menuOpen}
                    className="grid size-10 shrink-0 place-items-center rounded-xl border border-surface-border text-content-secondary transition hover:border-brand-500/50 hover:text-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 lg:hidden"
                  >
                    <Menu className="size-5" aria-hidden="true" />
                  </button>
                  <div className="flex min-w-0 justify-center">
                    <CompanyIdentityMark name={company.displayName} logos={logos} />
                  </div>
                </header>

                <OperationalPeriodBar today={scope.today} />

                <BusinessUnitSwitcher company={company} />

                {isRoot ? (
                  <div className="border-b border-brand-500/30 bg-brand-500/10">
                    <div className="flex flex-col gap-3 px-5 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-8">
                      <div className="flex items-center gap-2.5 text-xs">
                        <ShieldAlert className="size-4 shrink-0 text-brand-400" />
                        <strong className="text-brand-300">
                          Acesso root em {company.displayName}.
                        </strong>
                        <span className="hidden text-content-muted md:inline">
                          Todas as ações são auditadas.
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => void leave()}
                        disabled={leaving}
                        className="flex min-h-10 items-center gap-2 self-start rounded-lg px-3 py-2 text-xs font-semibold text-content/65 transition hover:bg-content/5 hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 disabled:cursor-wait disabled:opacity-50 sm:self-auto"
                      >
                        <ArrowLeft className="size-3.5" />
                        {leaving ? 'Saindo do tenant…' : 'Voltar ao console global'}
                      </button>
                    </div>
                  </div>
                ) : null}

                <main id="inicio" className="hue-page px-5 py-8 sm:px-8 sm:py-10">
                  <PageHelpProvider pathname={pathname} companyKey={company.key}>
                    {content ?? <Outlet />}
                  </PageHelpProvider>
                </main>
              </div>

              <TimeZoneSettings
                open={timeZoneSettingsOpen}
                onOpenChange={setTimeZoneSettingsOpen}
                user={user}
                onSaved={async () => {
                  await queryClient.invalidateQueries({ queryKey: sessionQuery.queryKey });
                }}
              />
            </div>
          </GuidesProvider>
        </ClosedPeriodProvider>
      </OperationalPeriodProvider>
    </CompanyScopeProvider>
  );
}

/// A engrenagem (ADR 0041): abre Configurações. Fica ao lado do ícone de cores, que continua
/// sendo o atalho rápido para a Aparência de lá.
function SettingsLink({
  companyKey,
  onNavigate,
  ...rest
}: {
  companyKey: string;
  onNavigate?: () => void;
}) {
  return (
    <Link
      {...rest}
      to="/companies/$companyKey/settings"
      params={{ companyKey }}
      onClick={() => onNavigate?.()}
      aria-label="Configurações"
      className="grid size-10 shrink-0 place-items-center rounded-xl border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60"
      activeProps={{
        'aria-current': 'page',
        className: 'border-brand-500/40 bg-brand-500/15 text-brand-400',
      }}
      inactiveProps={{
        className:
          'border-surface-border bg-surface-card text-content/65 hover:border-brand-500/60 hover:text-content',
      }}
    >
      <Settings className="size-4" aria-hidden="true" />
    </Link>
  );
}

/// O manual é parte da casca, não de um módulo comercial: qualquer pessoa autenticada na empresa
/// pode consultar como o produto funciona, inclusive o que depende de outra contratação.
function DocumentationLink({
  companyKey,
  onNavigate,
  ...rest
}: {
  companyKey: string;
  onNavigate?: () => void;
}) {
  return (
    <Link
      {...rest}
      to="/companies/$companyKey/documentation"
      params={{ companyKey }}
      onClick={() => onNavigate?.()}
      aria-label="Ajuda do sistema"
      className="grid size-10 shrink-0 place-items-center rounded-xl border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60"
      activeProps={{
        'aria-current': 'page',
        className: 'border-brand-500/40 bg-brand-500/15 text-brand-400',
      }}
      inactiveProps={{
        className:
          'border-surface-border bg-surface-card text-content/65 hover:border-brand-500/60 hover:text-content',
      }}
    >
      <BookOpen className="size-4" aria-hidden="true" />
    </Link>
  );
}

const CONSOLIDATED = '__consolidated__';

function BusinessUnitSwitcher({ company }: { company: NonNullable<SessionUser['currentTenant']> }) {
  const queryClient = useQueryClient();
  const switchUnit = useMutation({
    mutationFn: businessUnitApi.switch,
    onSuccess: async () => {
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== 'auth',
      });
      await refreshSession(queryClient);
    },
  });
  const units = company.businessUnits ?? [];
  if (units.length <= 1) return null;
  /// Com a rede crescendo, rolar atrás da unidade certa vira atrito: digitar filtra a lista.
  const selected = company.activeBusinessUnitId ?? CONSOLIDATED;
  const options = [
    { value: CONSOLIDATED, label: 'Todas as unidades — visão consolidada' },
    ...units.map((unit) => ({
      value: unit.id,
      label: unit.kind === 'SUPPORT' ? `${unit.displayName} · apoio` : unit.displayName,
    })),
  ];

  return (
    <div className="border-b border-surface-border bg-surface-card/70">
      <div className="px-5 py-3 sm:px-8">
        <Combobox
          className="max-w-sm"
          label="Unidade em visualização"
          placeholder="Buscar unidade"
          selectedLabel={options.find((option) => option.value === selected)?.label ?? ''}
          clearable={false}
          disabled={switchUnit.isPending}
          error={switchUnit.isError ? getApiError(switchUnit.error) : undefined}
          options={options}
          onSelect={(choice) => {
            if (choice.kind !== 'option' || choice.value === selected) return;
            switchUnit.mutate(choice.value === CONSOLIDATED ? null : choice.value);
          }}
        />
      </div>
    </div>
  );
}

/// O menu recolhido guarda a escolha na pessoa, como a aparência (ADR 0040, adendo): vale em
/// qualquer navegador. Antes da sessão responder, vale a cópia deste navegador.
function useNavigationCollapsed(
  user: SessionUser | null | undefined,
): [boolean, (collapsed: boolean) => void] {
  const queryClient = useQueryClient();
  const saved = user?.appearance?.navigation;
  const [local, setLocal] = useState<boolean>(() => readStoredNavigation() === 'collapsed');
  const collapsed = saved ? saved === 'collapsed' : local;

  function set(next: boolean) {
    const navigation = next ? 'collapsed' : 'expanded';
    setLocal(next);
    writeStoredNavigation(navigation);
    if (!user) return;
    const root = document.documentElement.dataset;
    const appearance: UserAppearance = {
      mode: user.appearance?.mode ?? (root.theme === 'light' ? 'light' : 'dark'),
      palette: user.appearance?.palette ?? ((root.palette as UserAppearance['palette']) || 'hue'),
      density: user.appearance?.density ?? (root.density === 'compact' ? 'compact' : 'comfortable'),
      navigation,
      ...(user.appearance?.appointmentLayout
        ? { appointmentLayout: user.appearance.appointmentLayout }
        : {}),
    };
    // A tela muda na hora; a gravação acompanha sem segurar o clique.
    queryClient.setQueryData(sessionQuery.queryKey, (current: SessionUser | null | undefined) =>
      current ? { ...current, appearance } : current,
    );
    void preferencesApi.updateUserAppearance(appearance).catch(() => undefined);
  }

  return [collapsed, set];
}

function readStoredNavigation(): string | null {
  try {
    return localStorage.getItem('hue-navigation');
  } catch {
    return null;
  }
}

function writeStoredNavigation(value: string) {
  try {
    localStorage.setItem('hue-navigation', value);
  } catch {
    // Sem armazenamento local, a escolha continua valendo pela sessão.
  }
}

function CompanyNavigationEntry({
  item,
  companyKey,
  onNavigate,
  compact,
}: {
  item: CompanyNavigationItem;
  companyKey: string;
  onNavigate: (() => void) | undefined;
  compact: boolean;
}) {
  if (item.kind === 'section') {
    return compact ? (
      <Tooltip content={item.section.label} side="right">
        <CompanyNavigationLink
          section={item.section}
          companyKey={companyKey}
          onNavigate={onNavigate}
          compact
        />
      </Tooltip>
    ) : (
      <CompanyNavigationLink
        section={item.section}
        companyKey={companyKey}
        onNavigate={onNavigate}
      />
    );
  }
  return compact ? (
    <CompanyNavigationFlyout item={item} companyKey={companyKey} />
  ) : (
    <CompanyNavigationGroup item={item} companyKey={companyKey} onNavigate={onNavigate} />
  );
}

/// Grupo no menu recolhido: o ícone abre, ao lado, um painel com as telas do grupo.
function CompanyNavigationFlyout({
  item,
  companyKey,
}: {
  item: Extract<CompanyNavigationItem, { kind: 'group' }>;
  companyKey: string;
}) {
  const pathname = useLocation({ select: (location) => location.pathname });
  const active = item.sections.some((section) =>
    pathname.startsWith(`/companies/${companyKey}/${section.key}`),
  );
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const Icon = item.sections[0]!.icon;
  const panelId = `company-navigation-flyout-${item.key}`;

  return (
    <>
      <Tooltip content={item.label} side="right">
        <button
          ref={anchorRef}
          type="button"
          aria-label={item.label}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          onClick={() => setOpen((current) => !current)}
          className={`grid h-11 w-full place-items-center rounded-xl border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 ${
            active
              ? 'border-brand-500/20 bg-brand-500/10 text-brand-400'
              : 'border-transparent text-content-muted hover:bg-content/5 hover:text-content'
          }`}
        >
          <Icon className="size-5" aria-hidden="true" />
        </button>
      </Tooltip>
      {open ? (
        <FloatingPanel
          anchorRef={anchorRef}
          onDismiss={() => setOpen(false)}
          id={panelId}
          role="menu"
          aria-label={item.label}
          className="w-60 space-y-1 rounded-2xl border border-surface-border bg-surface-card p-2 shadow-card"
        >
          <p className="px-3 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-[.08em] text-content-faint">
            {item.label}
          </p>
          {item.sections.map((section) => (
            <CompanyNavigationLink
              key={section.key}
              section={section}
              companyKey={companyKey}
              onNavigate={() => setOpen(false)}
              child
            />
          ))}
        </FloatingPanel>
      ) : null}
    </>
  );
}

function CompanyNavigationGroup({
  item,
  companyKey,
  onNavigate,
}: {
  item: Extract<CompanyNavigationItem, { kind: 'group' }>;
  companyKey: string;
  onNavigate: (() => void) | undefined;
}) {
  const pathname = useLocation({ select: (location) => location.pathname });
  const active = item.sections.some((section) =>
    pathname.startsWith(`/companies/${companyKey}/${section.key}`),
  );
  const [open, setOpen] = useState(active);
  const [lastPathname, setLastPathname] = useState(pathname);
  // Navegar para outra página recolhe o grupo aberto à mão; o grupo da página ativa segue aberto.
  if (lastPathname !== pathname) {
    setLastPathname(pathname);
    setOpen(false);
  }
  const expanded = active || open;
  const contentId = `company-navigation-${item.key}`;

  return (
    <div className="space-y-1">
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={contentId}
        onClick={() => setOpen((current) => !current)}
        className={`flex h-11 w-full items-center gap-3 rounded-xl border px-3 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 ${
          active
            ? 'border-brand-500/20 bg-brand-500/10 text-brand-400'
            : 'border-transparent text-content-muted hover:bg-content/5 hover:text-content'
        }`}
      >
        {(() => {
          const Icon = item.sections[0]!.icon;
          return <Icon className="size-5 shrink-0" aria-hidden="true" />;
        })()}
        <span className="min-w-0 flex-1 truncate text-left">{item.label}</span>
        <ChevronDown
          className={`size-4 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>
      {expanded ? (
        <div id={contentId} className="ml-5 space-y-1 border-l border-surface-border pl-2">
          {item.sections.map((section) => (
            <CompanyNavigationLink
              key={section.key}
              section={section}
              companyKey={companyKey}
              onNavigate={onNavigate}
              child
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function CompanyNavigationLink({
  section,
  companyKey,
  onNavigate,
  child = false,
  compact = false,
  ...rest
}: {
  section: CompanySection;
  companyKey: string;
  onNavigate: (() => void) | undefined;
  child?: boolean;
  /// Só o ícone; o nome vai no nome acessível e na dica de quem envolve o link.
  compact?: boolean;
}) {
  return (
    <Link
      {...rest}
      to={section.to}
      params={{ companyKey }}
      activeOptions={{ exact: section.key === 'home' }}
      onClick={() => onNavigate?.()}
      aria-label={compact ? section.label : undefined}
      className={`flex h-11 w-full items-center rounded-xl border text-sm font-medium transition ${
        compact ? 'justify-center px-0' : 'gap-3 px-3'
      } ${child ? 'text-[13px]' : ''}`}
      activeProps={{
        'aria-current': 'page',
        className: 'border-brand-500/20 bg-brand-500/15 text-brand-400',
      }}
      inactiveProps={{
        className: 'border-transparent text-content-muted hover:bg-content/5 hover:text-content',
      }}
    >
      <section.icon className={child ? 'size-4 shrink-0' : 'size-5 shrink-0'} aria-hidden="true" />
      {compact ? null : section.label}
    </Link>
  );
}
